import { useRef, useState, useEffect, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios'
import { faImage, faVideo, faFile, faDownload } from '@fortawesome/free-solid-svg-icons';
import Header from './Header';
import Footer from './Footer';

const Announcements = () => {
    const server_app_api_php = window.SERVER_APP_API_PHP
    const [announcements, setAnnouncements] = useState([]);
    const [expandedAnnouncement, setExpandedAnnouncement] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const toggleContentLatestAnnouncement = (index) => {
        setExpandedAnnouncement(prevState => ({
            ...prevState,
            [index]: !prevState[index]
        }));
    };

    const fetchAnnouncements = async () => {
        setIsLoading(true);
        const formData = new FormData();
        formData.append('apiType', 'get-announcements');
        
        try {
            const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setAnnouncements(response.data);
        } catch (error) {
            console.log('An error occurred while retrieving announcements.');
        } finally {
            setIsLoading(false);
        }
    };

    const downloadFile = async (filepath) => {
        try {
            const response = await axios.get(`${server_app_api_php}/server/downloadReports.php?file=${filepath}`, {
                responseType: 'blob'
            });
            
            if (response.status === 200) {
                const blob = new Blob([response.data], { type: 'application/octet-stream' });
                const downloadLink = document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(blob);
                downloadLink.download = filepath.split('/').pop();
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            }
        } catch (error) {
            alert('Error downloading file. Please try again.');
        }
    };

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-cyan-50">
            <Header />
            
            <main className="pt-24">
                <div className="max-w-7xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-bold text-gray-800 mb-4">Announcements</h1>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Stay updated with the latest announcements and important information from Woodlands Conference AY Ministries.
                        </p>
                    </div>

                    {/* Loading State */}
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                            <p className="text-gray-600">Loading announcements...</p>
                        </div>
                    ) : announcements.length > 0 ? (
                        <div className="space-y-8">
                            {announcements.map((announcement, index) => (
                                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden transform hover:shadow-xl transition-all duration-300">
                                    {/* Date Badge */}
                                    <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-white font-semibold">
                                                {new Date(announcement.created_at).toLocaleDateString('en-US', {
                                                    weekday: 'long',
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </span>
                                            <span className="text-blue-100 text-sm bg-white/20 px-3 py-1 rounded-full">
                                                Announcement
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Content */}
                                    <div className="p-6">
                                        <h3 className="text-2xl font-bold text-gray-800 mb-4">{announcement.title}</h3>
                                        
                                        <div className="text-gray-600 leading-relaxed mb-6">
                                            <p>
                                                {expandedAnnouncement[index] 
                                                    ? announcement.description 
                                                    : announcement.description.split(' ').slice(0, 50).join(' ')}
                                                {announcement.description.split(' ').length > 50 && (
                                                    <button
                                                        onClick={() => toggleContentLatestAnnouncement(index)}
                                                        className="ml-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors duration-300"
                                                    >
                                                        {expandedAnnouncement[index] ? 'Show Less' : 'Read More'}
                                                    </button>
                                                )}
                                            </p>
                                        </div>

                                        {/* Attachments */}
                                        {(announcement.media_urlsImages?.length > 0 || 
                                          announcement.media_urlsVideos?.length > 0 || 
                                          announcement.media_urlsDocuments?.length > 0) && (
                                            <div className="mt-6 pt-6 border-t border-gray-200">
                                                <h4 className="font-semibold text-gray-700 mb-4 flex items-center">
                                                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                                    </svg>
                                                    Attachments
                                                </h4>
                                                
                                                <div className="flex flex-wrap gap-3">
                                                    {announcement.media_urlsImages?.length > 0 && (
                                                        <button
                                                            onClick={() => downloadFile(announcement.media_urlsImages)}
                                                            className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2 rounded-lg transition-colors duration-300 group"
                                                        >
                                                            <FontAwesomeIcon icon={faImage} className="text-blue-600" />
                                                            <span>Images</span>
                                                            <FontAwesomeIcon icon={faDownload} className="text-blue-500 group-hover:text-blue-700" />
                                                        </button>
                                                    )}
                                                    
                                                    {announcement.media_urlsVideos?.length > 0 && (
                                                        <button
                                                            onClick={() => downloadFile(announcement.media_urlsVideos)}
                                                            className="flex items-center space-x-2 bg-purple-50 hover:bg-purple-100 text-purple-700 px-4 py-2 rounded-lg transition-colors duration-300 group"
                                                        >
                                                            <FontAwesomeIcon icon={faVideo} className="text-purple-600" />
                                                            <span>Videos</span>
                                                            <FontAwesomeIcon icon={faDownload} className="text-purple-500 group-hover:text-purple-700" />
                                                        </button>
                                                    )}
                                                    
                                                    {announcement.media_urlsDocuments?.length > 0 && (
                                                        <button
                                                            onClick={() => downloadFile(announcement.media_urlsDocuments)}
                                                            className="flex items-center space-x-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-4 py-2 rounded-lg transition-colors duration-300 group"
                                                        >
                                                            <FontAwesomeIcon icon={faFile} className="text-emerald-600" />
                                                            <span>Documents</span>
                                                            <FontAwesomeIcon icon={faDownload} className="text-emerald-500 group-hover:text-emerald-700" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
                                <svg className="w-12 h-12 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-700 mb-3">No Announcements Yet</h3>
                            <p className="text-gray-500 max-w-md mx-auto">
                                There are no announcements available at the moment. Please check back later for updates.
                            </p>
                        </div>
                    )}

                    {/* Stats */}
                    <div className="mt-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-white mb-2">{announcements.length}</div>
                                <div className="text-blue-100">Total Announcements</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-white mb-2">
                                    {announcements.filter(a => a.media_urlsImages?.length > 0).length}
                                </div>
                                <div className="text-blue-100">With Images</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-white mb-2">
                                    {announcements.filter(a => a.media_urlsVideos?.length > 0).length}
                                </div>
                                <div className="text-blue-100">With Videos</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-white mb-2">24/7</div>
                                <div className="text-blue-100">Access</div>
                            </div>
                        </div>
                    </div>

                    {/* Subscribe Section */}
                    <div className="mt-12 bg-white rounded-2xl shadow-lg p-8">
                        <div className="text-center mb-8">
                            <h3 className="text-2xl font-bold text-gray-800 mb-3">Never Miss an Update</h3>
                            <p className="text-gray-600 max-w-md mx-auto">
                                Subscribe to get notified about new announcements directly to your email.
                            </p>
                        </div>
                        
                        <div className="max-w-md mx-auto">
                            <div className="flex gap-3">
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <button className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 font-semibold">
                                    Subscribe
                                </button>
                            </div>
                            <p className="text-gray-500 text-sm text-center mt-3">
                                We respect your privacy. Unsubscribe at any time.
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Announcements;